/**
 * Resources section — file downloads, optionally gated by a contact form.
 *
 * Two modes, decided by Liquid at render time:
 *   1. Lead capture form block PRESENT → modal markup rendered.
 *      Click on a trigger opens the modal, populates hidden fields, and on
 *      successful contact-form submit triggers the file download.
 *   2. Lead capture form block ABSENT → no modal markup rendered.
 *      Click on a trigger downloads the file directly, no form gate.
 *
 * Form submits via fetch() to Shopify's /contact endpoint so the page
 * doesn't reload. reCAPTCHA tokens are part of the serialized FormData
 * so the server still validates them.
 */
(function () {
  'use strict';

  /**
   * Create or update a Shopify customer record with marketing consent + a
   * tag identifying which resource they requested. Submits to /contact with
   * form_type=customer (Shopify's newsletter-signup endpoint). Fire-and-forget:
   * we never await the response since the visitor's download is already
   * underway.
   */
  function subscribeCustomer(formEl, resourceName) {
    var emailEl = formEl.querySelector('[name="contact[email]"]');
    if (!emailEl || !emailEl.value) return;

    var nameEl = formEl.querySelector('[name="contact[Name]"]');
    var phoneEl = formEl.querySelector('[name="contact[Phone]"]');
    var consentEl = formEl.querySelector('[data-marketing-consent]');

    var data = new FormData();
    data.append('form_type', 'customer');
    data.append('utf8', '✓');
    data.append('contact[email]', emailEl.value);
    if (nameEl && nameEl.value) data.append('contact[first_name]', nameEl.value);
    if (phoneEl && phoneEl.value) data.append('contact[phone]', phoneEl.value);
    data.append('contact[accepts_marketing]', consentEl && consentEl.checked ? 'true' : 'false');

    var tags = ['resource-download'];
    if (resourceName) tags.push('resource:' + resourceName);
    data.append('contact[tags]', tags.join(', '));
    if (resourceName) data.append('contact[note]', 'Requested resource: ' + resourceName);

    var endpoint = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      ? window.Shopify.routes.root + 'contact'
      : '/contact';

    fetch(endpoint, {
      method: 'POST',
      body: data,
      redirect: 'follow',
      headers: { 'Accept': 'text/html' }
    }).catch(function () { /* swallow — best-effort */ });
  }

  function triggerDownload(url, filename) {
    if (!url) return;
    var a = document.createElement('a');
    a.href = url;
    if (filename) a.download = filename;
    a.rel = 'noopener';
    // Some browsers ignore `download` for cross-origin URLs — that's OK,
    // they'll open the file inline which is still the desired UX.
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function initSection(section) {
    var triggers = section.querySelectorAll('[data-resource-trigger]');
    if (!triggers.length) return;

    var modal = section.querySelector('[data-resource-modal]');

    // ===== Direct-download path (no Lead capture form block) =====
    if (!modal) {
      triggers.forEach(function (btn) {
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          if (btn.hasAttribute('disabled')) return;
          var url = btn.getAttribute('data-resource-url') || '';
          var filename = btn.getAttribute('data-resource-filename') || '';
          triggerDownload(url, filename);
        });
      });
      return;
    }

    // ===== Gated path (modal present) =====
    var form = modal.querySelector('form');
    var nameField = modal.querySelector('[data-resource-field]');
    var urlField = modal.querySelector('[data-resource-url-field]');
    var statusEl = modal.querySelector('.resources-modal__status');
    var submitBtn = form ? form.querySelector('[type="submit"]') : null;
    var closeBtns = modal.querySelectorAll('[data-close]');

    var openerEl = null;

    function openModal(trigger) {
      openerEl = trigger;
      var name = trigger.getAttribute('data-resource-name') || '';
      var url = trigger.getAttribute('data-resource-url') || '';
      var filename = trigger.getAttribute('data-resource-filename') || '';

      if (nameField) nameField.value = name;
      if (urlField) urlField.value = url;
      modal.dataset.resourceName = name;
      modal.dataset.resourceUrl = url;
      modal.dataset.resourceFilename = filename;

      modal.setAttribute('open', '');
      document.body.classList.add('resources-modal-open');
      if (statusEl) {
        statusEl.hidden = true;
        statusEl.textContent = '';
        statusEl.removeAttribute('data-state');
      }
      // Focus the dialog container (tabindex="-1") rather than the first field —
      // auto-focusing the Name input triggers its floating label to float up
      // immediately, which combined with browser autofill suggestions leaves
      // the user looking at a field with no visible label. Focusing the dialog
      // still gives screen readers proper modal context.
      setTimeout(function () {
        var dialogEl = modal.querySelector('.resources-modal__content');
        if (dialogEl && typeof dialogEl.focus === 'function') dialogEl.focus();
      }, 50);
    }

    function closeModal() {
      modal.removeAttribute('open');
      document.body.classList.remove('resources-modal-open');
      if (openerEl && typeof openerEl.focus === 'function') openerEl.focus();
      openerEl = null;
    }

    function showStatus(message, state, html) {
      if (!statusEl) return;
      if (html) {
        statusEl.innerHTML = message;
      } else {
        statusEl.textContent = message;
      }
      statusEl.setAttribute('data-state', state || 'info');
      statusEl.hidden = false;
    }

    // ===== Trigger click handlers =====
    triggers.forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        if (btn.hasAttribute('disabled')) return;
        openModal(btn);
      });
    });

    // ===== Close handlers =====
    closeBtns.forEach(function (b) {
      b.addEventListener('click', function () { closeModal(); });
    });
    modal.addEventListener('click', function (event) {
      // Click on backdrop (the modal element itself, not its dialog content)
      if (event.target === modal) closeModal();
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && modal.hasAttribute('open')) closeModal();
    });

    // ===== Form submit =====
    // Override the native form submit so Shopify's reCAPTCHA JS cannot
    // trigger a full page reload via form.submit().
    if (!form) return;
    var nativeSubmit = form.submit.bind(form);
    form.submit = function () {
      /* blocked — our fetch handler below takes over */
    };

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.dataset.originalLabel = submitBtn.textContent;
        submitBtn.textContent = (submitBtn.dataset.submittingLabel || 'Submitting…');
      }

      var formData = new FormData(form);
      var endpoint = form.action || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root + 'contact' : '/contact');

      fetch(endpoint, {
        method: 'POST',
        body: formData,
        redirect: 'follow',
        headers: { 'Accept': 'text/html' }
      })
        .then(function () {
          // Client-side validation already guards against bad data.
          // If the fetch didn't throw, the form was submitted to Shopify.
          return { success: true };
        })
        .then(function (result) {
          if (result.success) {
            var fileUrl = modal.dataset.resourceUrl || '';
            var fileName = modal.dataset.resourceFilename || '';
            var resourceName = modal.dataset.resourceName || '';

            // Fire-and-forget: create/update a customer record with marketing consent.
            try { subscribeCustomer(form, resourceName); } catch (e) { /* no-op */ }

            // Replace modal content with a download button the user can click directly
            var dialogEl = modal.querySelector('.resources-modal__content');
            if (dialogEl && fileUrl) {
              var successMsg = modal.getAttribute('data-success-message') || 'Thanks!';
              dialogEl.innerHTML =
                '<button type="button" data-close class="resources-modal__close" aria-label="Close" style="position:absolute;top:1rem;right:1rem;background:none;border:none;cursor:pointer;padding:0.5rem;">&times;</button>' +
                '<div style="text-align:center;padding:2rem 1rem;">' +
                  '<p style="margin:0 0 1.5rem;font-size:1.1em;">' + successMsg + '</p>' +
                  '<a href="' + fileUrl + '" download="' + fileName + '" target="_blank" rel="noopener" class="button" style="text-decoration:none;">Download ' + resourceName + '</a>' +
                '</div>';
              // Re-bind the new close button
              dialogEl.querySelector('[data-close]').addEventListener('click', closeModal);
            }
            form.reset();
          } else {
            var errorMsg = modal.getAttribute('data-error-message') || 'Something went wrong. Please try again.';
            showStatus(errorMsg, 'error');
          }
        })
        .catch(function () {
          var errorMsg = modal.getAttribute('data-error-message') || 'Something went wrong. Please try again.';
          showStatus(errorMsg, 'error');
        })
        .finally(function () {
          if (submitBtn) {
            submitBtn.disabled = false;
            if (submitBtn.dataset.originalLabel) {
              submitBtn.textContent = submitBtn.dataset.originalLabel;
            }
          }
        });
    });
  }

  function init() {
    document.querySelectorAll('[data-resources-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize when the Shopify section is re-rendered in the customizer
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('[data-resources-section]');
    if (section) initSection(section);
  });
})();